import type { ReactNode } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Download, Filter, RefreshCw } from 'lucide-react';
import type { SavedDashboardFilter } from './types';

interface DashboardFilterBarProps {
  savedFilters: SavedDashboardFilter[];
  activeFilterPresetId: number | null;
  onLoadPreset: (preset: SavedDashboardFilter, skipFetch?: boolean) => void;
  showFilters: boolean;
  setShowFilters: (show: boolean) => void;
  activeFilterCount: number;
  onRefresh: () => void;
  onExportReport?: () => void;
  exporting?: boolean;
  /** The filter sections and footer, shown inside the card while it is open. */
  children?: ReactNode;
}

/**
 * CR-6401: the filter header bar.
 *
 * Split out of DashboardHeader so the page follows the agreed layout:
 * page header -> tab bar -> filter header -> filter sections -> filter footer.
 * The bar stays visible when the filter sections are collapsed, so the active
 * filter count and the Refresh / Export / Filters actions remain reachable.
 *
 * CR-6402 rework: the same card every other filter surface uses (PO Lines
 * `CustomerSectionedFilters`, Quality `NCRFiltersBar`) — "Filter …" title on
 * the left; Refresh and Export as small outline buttons with icons and a
 * "Filters" toggle carrying the active count as a badge on the right; the
 * sections open inside the same card. The saved-filter picker is dashboard
 * only and is kept in the header, sized to the buttons beside it.
 */
export function DashboardFilterBar({
  savedFilters,
  activeFilterPresetId,
  onLoadPreset,
  showFilters,
  setShowFilters,
  activeFilterCount,
  onRefresh,
  onExportReport,
  exporting,
  children,
}: DashboardFilterBarProps) {
  return (
    <Card>
      <CardHeader className="py-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <CardTitle className="text-xl">Filter Dashboard</CardTitle>
          <div className="flex items-center gap-2 flex-wrap">
            {savedFilters.length > 0 && (
              <select
                aria-label="Load saved filter"
                value={activeFilterPresetId?.toString() || ''}
                onChange={(e) => {
                  const preset = savedFilters.find((f) => f.id.toString() === e.target.value);
                  if (preset) onLoadPreset(preset);
                }}
                className="h-8 rounded-md border border-border bg-background px-2.5 text-sm hover:bg-muted"
              >
                <option value="" disabled>Load saved filter...</option>
                {savedFilters.map((filter) => (
                  <option key={filter.id} value={filter.id}>
                    {filter.is_default && '⭐ '}{filter.name}
                  </option>
                ))}
              </select>
            )}
            <Button type="button" variant="outline" size="sm" onClick={onRefresh}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onExportReport}
              disabled={exporting}
            >
              <Download className="h-4 w-4 mr-2" />
              Export
            </Button>
            <button
              type="button"
              onClick={() => setShowFilters(!showFilters)}
              aria-expanded={showFilters}
              className={`inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md border transition-colors ${
                showFilters
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'border-border hover:bg-muted'
              }`}
            >
              <Filter className="h-4 w-4" />
              Filters
              {activeFilterCount > 0 && (
                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                  showFilters
                    ? 'bg-primary-foreground/20 text-primary-foreground'
                    : 'bg-primary text-primary-foreground'
                }`}>
                  {activeFilterCount}
                </span>
              )}
            </button>
          </div>
        </div>
      </CardHeader>

      {showFilters && children && (
        <CardContent className="p-0">{children}</CardContent>
      )}
    </Card>
  );
}
